import React, { useEffect, useState } from 'react';
import { Box, Link } from '@mui/material';
import { paymentApi } from '../../services';
import { StatusPill, DetailRow } from './DetailRow';

export interface PaymentDetailsValue {
  leadId?: string;
  paymentId?: string;
  /** Gateway that took the payment; older responses omit it (Cashfree). */
  provider?: string;
}

export const getPaymentDetails = (
  data: Record<string, any>,
) =>
  Object.values(data || {}).find(
    (value: any) =>
      value &&
      typeof value === 'object' &&
      value.leadId &&
      value.paymentId,
  ) as PaymentDetailsValue | undefined;

interface PaymentDetailsProps {
  tenantId?: string;
  payment: PaymentDetailsValue;
  compact?: boolean;
}

export const PaymentDetails: React.FC<
  PaymentDetailsProps
> = ({ tenantId, payment, compact }) => {
  const [paymentRecord, setPaymentRecord] =
    useState<any>(null);

  useEffect(() => {
    if (!tenantId || !payment?.paymentId) return;
    setPaymentRecord(null);
    paymentApi
      .getPaymentRecord(tenantId, payment.paymentId, payment.provider || 'cashfree')
      .then((result) =>
        setPaymentRecord(result?.data || result),
      )
      .catch(() => setPaymentRecord(null));
  }, [tenantId, payment?.paymentId, payment?.provider]);

  const status = paymentRecord?.status;

  return (
    <Box>
      <StatusPill
        label={status ? status.toUpperCase() : 'Loading status'}
        tone={status === 'paid' ? 'success' : status ? 'warning' : 'neutral'}
        loading={!status}
      />
      <DetailRow label="Payment ID:">{payment.paymentId || '—'}</DetailRow>
      <DetailRow label="Lead ID:">{payment.leadId || '—'}</DetailRow>
      {paymentRecord && !compact && (
        <>
          <DetailRow label="Amount:">
            ₹{paymentRecord.amount ?? '—'} {paymentRecord.currency || 'INR'}
          </DetailRow>
          <DetailRow label="Gateway order:">
            {paymentRecord.cashfreeId || paymentRecord.orderId || paymentRecord.metadata?.orderId || '—'}
          </DetailRow>
          <DetailRow label="Provider:">{payment.provider || 'cashfree'}</DetailRow>
          <DetailRow label="Payment time:">
            {paymentRecord.createdAt
              ? new Date(paymentRecord.createdAt).toLocaleString('en-IN', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                })
              : '—'}
          </DetailRow>
          {paymentRecord.invoiceLink && (
            <DetailRow label="Invoice:">
              <Link
                href={paymentRecord.invoiceLink}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
              >
                View Invoice
              </Link>
            </DetailRow>
          )}
        </>
      )}
    </Box>
  );
};
